import {Alert, Form, FormField, FormSubmitHandler, showNotification} from 'platform/components';
import {Grid, GridItem, Show, VStack} from 'platform/foundation';

import {isNil, isNotNil} from 'ramda';

import {
  GetWarehouseDispensingUnitPresetResponse,
  usePatchDispensingUnitPresetMutation,
  usePostDispensingUnitPresetMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {SettingsFooter} from '@dms/settings/shared';
import {handleApiError, useDuplicateErrorHandler} from '@dms/shared';

import {ApiError, Nullish, RequiredTestIdProps, suffixTestId, useNavigate} from 'shared/web';

import {
  DispensingUnitsPresetsForm,
  DispensingUnitsPresetsFormSchema,
} from '../types/DispensingUnitsPresetsForm';

export interface DispensingUnitsPresetsDetailFormProps extends RequiredTestIdProps {
  dispensingUnitPreset: GetWarehouseDispensingUnitPresetResponse | Nullish;
  isEditDisabled: boolean;
}

export function DispensingUnitsPresetsDetailForm(props: DispensingUnitsPresetsDetailFormProps) {
  const navigate = useNavigate();

  const {duplicateError: isDuplicated, duplicateErrorHandler} = useDuplicateErrorHandler();

  const [postDispensingUnitPreset] = usePostDispensingUnitPresetMutation();
  const [patchDispensingUnitPreset] = usePatchDispensingUnitPresetMutation();

  const handleNavigateBack = () => {
    navigate(settingsRoutes.warehousesDispensingUnitsPresets);
  };

  const handleSubmit: FormSubmitHandler<DispensingUnitsPresetsForm> = async (
    formValues,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    if (isNotNil(props.dispensingUnitPreset)) {
      return await patchDispensingUnitPreset({
        codeListId: props.dispensingUnitPreset.id,
        body: {
          value: formValues.value,
        },
      })
        .unwrap()
        .then(() => {
          if (blockerContext?.wasSubmittedFromBlocker) {
            return;
          }
          handleNavigateBack();
        })
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );
    }

    await postDispensingUnitPreset({
      value: formValues.value,
    })
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.successfullyCreated'));
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        handleNavigateBack();
      })
      .catch(duplicateErrorHandler);
  };

  const defaultValues: Partial<DispensingUnitsPresetsForm> = {
    value: props.dispensingUnitPreset?.value,
  };

  return (
    <Form<DispensingUnitsPresetsForm>
      experimentalZodSchema={DispensingUnitsPresetsFormSchema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      isDisabled={props.isEditDisabled}
      shouldWatchForUnsavedChanges
    >
      {(control, _formApi, navigateWithoutBlocker) => (
        <VStack spacing={4} data-testid={props['data-testid']}>
          <Show when={isDuplicated}>
            <Alert
              variant="error"
              title={i18n.t('entity.warehouse.notifications.dispensingUnitPresetAlreadyExists')}
              data-testid={suffixTestId('dispensingUnitPresetAlreadyExists', props)}
            />
          </Show>
          <Grid columns={2}>
            <GridItem span={1}>
              <FormField
                control={control}
                isRequired
                isStepperVisible
                type="number"
                name="value"
                decimalPlaces={4}
                label={i18n.t('entity.warehouse.labels.dispensingUnit')}
                data-testid={suffixTestId('inputs.dispensingUnit', props)}
              />
            </GridItem>
          </Grid>
          <SettingsFooter
            actions={[
              {
                type: 'button',
                variant: 'secondary',
                title: isNil(props.dispensingUnitPreset)
                  ? i18n.t('general.actions.discard')
                  : i18n.t('general.actions.discardChanges'),
                onClick: () =>
                  navigateWithoutBlocker(settingsRoutes.warehousesDispensingUnitsPresets),
                'data-testid': suffixTestId('actions.discard', props),
              },
              {
                type: 'form-button',
                control,
                buttonType: 'submit',
                variant: 'primary',
                title: isNil(props.dispensingUnitPreset)
                  ? i18n.t('general.actions.save')
                  : i18n.t('general.actions.saveChanges'),
                'data-testid': suffixTestId('actions.submit', props),
              },
            ]}
          />
        </VStack>
      )}
    </Form>
  );
}
